/**
 * `/cash/bank-accounts/$bankAccountId/edit` — S32 edit flow.
 *
 * **Strict-fidelity rebuild** per playbook §6. Page-scoped CSS lives in
 * `./bank-edit-page.css`, lifted verbatim from `prototype/cash/bank-edit.html`
 * (lines 11-1047). JSX mirrors the prototype's tag + class hierarchy:
 *
 *   .composer-head (back arrow + h1 with .acc-num + .role-pill + .sub
 *     with dot separators) + .save-status + .composer-head-actions
 *     (Cancel + Save changes)
 *   .composer-body (1.5fr | 1fr grid):
 *     .composer-form > .section × N (each .section-head with .step
 *       number + h3 + .sub + .right pills + .section-body + optional
 *       .section-foot)
 *     .composer-aside > .aside-card (live preview, audit, danger)
 *
 * V1.0 functional sections: Identity (rename) + Danger zone (archive).
 * V1.x sections: Bank & KYC details (IFSC + branch + holder), Sync
 * method, Defaults, Balance & opening (read-only after create).
 *
 * @see prototype/cash/bank-edit.html
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Clock3,
  CreditCard,
  Eye,
  FileText,
  FileUp,
  Landmark,
  LoaderCircle,
  Lock,
  Pencil,
  RefreshCcw,
  Search,
  Shield,
  Sparkles,
  Wallet,
  X,
} from "lucide-react";
import { Alert, ConfirmDialog, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import type { bankAccounts as bankAccountsContract } from "@flint/api-contract";
import { STEP_UP_ACTIONS } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { useStepUpGate } from "../../../../features/auth/index.ts";
import "./bank-edit-page.css";

type AccountWire = bankAccountsContract.GetBankAccountOutput["account"];

export const Route = createFileRoute("/(workspace)/cash/bank-accounts/$bankAccountId/edit")({
  component: EditBankAccountPage,
});

function EditBankAccountPage() {
  const { bankAccountId } = Route.useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const accountQ = trpc.bankAccounts.get.useQuery({ bankAccountId });

  const [name, setName] = useState("");
  const [originalName, setOriginalName] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (accountQ.data) {
      setName(accountQ.data.account.name);
      setOriginalName(accountQ.data.account.name);
    }
  }, [accountQ.data]);

  const rename = trpc.bankAccounts.rename.useMutation({
    onSuccess: async () => {
      await utils.bankAccounts.get.invalidate({ bankAccountId });
      await utils.bankAccounts.list.invalidate();
      navigate({ to: "/cash/bank-accounts/$bankAccountId", params: { bankAccountId } });
    },
    onError: (err) => setErrorMsg(err.message),
  });

  /** Change-credentials (account number + IFSC) — step-up gated mutation
      from ADR-0008. On success refreshes the row (so the cooling-off
      banner appears automatically) and closes the dialog. */
  const changeBankDetails = trpc.bankAccounts.changeBankDetails.useMutation({
    onSuccess: async () => {
      await utils.bankAccounts.get.invalidate({ bankAccountId });
      await utils.bankAccounts.list.invalidate();
      cancelCredsModal();
    },
    onError: (err) => setCredsModalError(err.message),
  });

  // Step-up gate for the sensitive account-number/IFSC change (E03 fix):
  // founder OTP must be verified before the mutation runs. The server enforces
  // the same gate via stepUpFounderProcedureFor(BANK_ACCOUNTS_CHANGE_BANK_DETAILS),
  // so a direct API call without step-up evidence is now rejected (UNAUTHORIZED).
  const credsStepUpGate = useStepUpGate(STEP_UP_ACTIONS.BANK_ACCOUNTS_CHANGE_BANK_DETAILS);

  // ── Credentials slide-over / dialog state ──────────────────
  const [dormantOpen, setDormantOpen] = useState(false);
  const [credsModalOpen, setCredsModalOpen] = useState(false);
  const [credsModalError, setCredsModalError] = useState<string | null>(null);
  const [draftAccountNumber, setDraftAccountNumber] = useState("");
  const [draftIfsc, setDraftIfsc] = useState("");
  const [draftIfscLookup, setDraftIfscLookup] = useState<{
    bank: string;
    branch: string;
    city: string;
    state: string;
    micr: string | null;
    swift: string | null;
  } | null>(null);
  const [draftIfscError, setDraftIfscError] = useState<string | null>(null);
  const [draftIfscLoading, setDraftIfscLoading] = useState(false);

  const draftIfscIsWellFormed = /^[A-Z]{4}0[A-Z0-9]{6}$/.test(draftIfsc);
  const draftReady =
    draftAccountNumber.trim().length >= 6 &&
    draftAccountNumber.trim().length <= 64 &&
    draftIfscIsWellFormed;

  const openCredsModal = () => {
    // Start from blank — the operator must re-type both even on a
    // change. This matches ADR-0008's intent: no partial visibility of
    // existing credentials inside the dialog.
    setDraftAccountNumber("");
    setDraftIfsc("");
    setDraftIfscLookup(null);
    setDraftIfscError(null);
    setCredsModalError(null);
    setCredsModalOpen(true);
  };

  const cancelCredsModal = () => {
    setCredsModalOpen(false);
    setDraftIfscError(null);
    setDraftIfscLoading(false);
    setCredsModalError(null);
  };

  const saveCredsModal = async () => {
    if (!draftReady) return;
    setCredsModalError(null);
    try {
      await credsStepUpGate.execute(
        () =>
          changeBankDetails.mutateAsync({
            bankAccountId,
            accountNumberPlaintext: draftAccountNumber.trim(),
            ifscPlaintext: draftIfsc.trim().toUpperCase(),
          }),
        {
          title: "Change bank account details",
          description:
            "Founder OTP is required. After save, a 24-hour cooling-off window applies before the new details take effect.",
        },
      );
    } catch (err) {
      setCredsModalError(err instanceof Error ? err.message : "Couldn't save bank details.");
    }
  };

  const fetchDraftIfsc = async () => {
    if (!draftIfscIsWellFormed) return;
    setDraftIfscLoading(true);
    setDraftIfscError(null);
    try {
      const r = await utils.ifsc.lookup.fetch({ ifsc: draftIfsc });
      setDraftIfscLookup({
        bank: r.bank,
        branch: r.branch,
        city: r.city,
        state: r.state,
        micr: r.micr,
        swift: r.swift,
      });
    } catch (err) {
      setDraftIfscLookup(null);
      setDraftIfscError(err instanceof Error ? err.message : "IFSC lookup failed.");
    } finally {
      setDraftIfscLoading(false);
    }
  };

  const archive = trpc.bankAccounts.archive.useMutation({
    onSuccess: async () => {
      await utils.bankAccounts.list.invalidate();
      navigate({ to: "/cash/bank-accounts" });
    },
    onError: (err) => setErrorMsg(err.message),
  });

  const isDirty = name !== originalName && name.trim().length > 0;
  const saveStatus: "saved" | "dirty" | "saving" = rename.isPending
    ? "saving"
    : isDirty
      ? "dirty"
      : "saved";

  if (accountQ.isLoading) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Cash" }]}>
        <p style={{ padding: 24, color: "var(--muted)" }}>Loading…</p>
      </WorkspaceShell>
    );
  }
  if (accountQ.error || !accountQ.data) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Cash" }]}>
        <div style={{ padding: 24 }}>
          <Alert
            intent="bad"
            title="Couldn't load account"
            description={accountQ.error?.message ?? "Unknown"}
          />
        </div>
      </WorkspaceShell>
    );
  }
  const account = accountQ.data.account;
  const parsed = parseAccountName(account.name);
  const ava = avatarFor(account);

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Cash" },
        { label: "Bank accounts", to: "/cash/bank-accounts" },
        { label: account.name, to: "/cash/bank-accounts/$bankAccountId" },
        { label: "Edit" },
      ]}
      /* Composer surfaces own their chrome — the sticky composer-head must
         touch the top of the scroll viewport, full-width, with no shell
         padding or max-width cap eating into it. */
      viewportClassName="!p-0"
      contentClassName="!max-w-full !mx-0"
    >
      <div className="bank-edit-page">
        {/* ── Composer head ─────────────────────────────── */}
        <header className="composer-head">
          <Link
            to="/cash/bank-accounts/$bankAccountId"
            params={{ bankAccountId }}
            className="composer-head-back"
            aria-label="Back to account detail"
          >
            <ArrowLeft size={16} strokeWidth={2} aria-hidden />
          </Link>
          <div className="composer-head-text">
            <h1>
              {parsed.bank}
              {account.accountNumberLast4 ? (
                <span className="acc-num">···{account.accountNumberLast4}</span>
              ) : parsed.acc ? (
                <span className="acc-num">{parsed.acc}</span>
              ) : null}
              <span className="role-pill">{labelForType(account.type)}</span>
            </h1>
            <p className="sub">
              <span>Edit display name, balance &amp; defaults</span>
              <span className="dot" aria-hidden="true" />
              <span>Sync · V1.x</span>
              <span className="dot" aria-hidden="true" />
              <span>
                Last edited{" "}
                <strong style={{ color: "var(--ink-2-local)", fontWeight: 600 }}>
                  {formatRelativeTime(account.updatedAt)}
                </strong>
              </span>
              {account.bankDetailsChangedAt ? (
                <>
                  <span className="dot" aria-hidden="true" />
                  <span>
                    Credentials changed{" "}
                    <strong style={{ color: "var(--ink-2-local)", fontWeight: 600 }}>
                      {formatDate(account.bankDetailsChangedAt.slice(0, 10))}
                    </strong>
                  </span>
                </>
              ) : null}
            </p>
          </div>
          <SaveStatus status={saveStatus} />
          <div className="composer-head-actions">
            <Link
              to="/cash/bank-accounts/$bankAccountId"
              params={{ bankAccountId }}
              className="btn-secondary"
            >
              Cancel
            </Link>
            <button
              type="button"
              className="btn-primary"
              disabled={!isDirty || rename.isPending}
              onClick={() => {
                setErrorMsg(null);
                rename.mutate({ bankAccountId, name: name.trim() });
              }}
            >
              <Check size={13} strokeWidth={2} aria-hidden />
              {rename.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        </header>

        <div className="composer-body">
          {/* ── LEFT: form sections ───────────────────────── */}
          <main className="composer-form">
            {errorMsg ? <Alert intent="bad" title="Couldn't save" description={errorMsg} /> : null}

            {/* Section 1 — Identity (mirror prototype/cash/bank-edit.html lines 1191-1316) */}
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>
                    <span className="step">1</span> Account identity
                  </h3>
                  <p className="sub">
                    Display name, type, currency and the chart-of-account code this maps to.
                  </p>
                </div>
                <div className="right">
                  <span className="pill ok">Editable</span>
                </div>
              </div>
              <div className="section-body">
                {/* Row 1 — Display name (editable) + Internal code (V1.x disabled) */}
                <div className="field-grid cols-2">
                  <div className="field">
                    <label>
                      Display name <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      className="field-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. HDFC Bank · Current ···4421"
                    />
                    <span className="field-help">
                      Shown in lists, invoices, payment receipts and Ask Flint answers.
                    </span>
                  </div>
                  <div className="field is-mono">
                    <label>
                      Internal code{" "}
                      <span
                        style={{
                          marginLeft: 4,
                          padding: "1px 6px",
                          borderRadius: 3,
                          background: "var(--bg-local)",
                          color: "var(--muted-local)",
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.06,
                          textTransform: "uppercase",
                          border: "1px solid var(--line-local)",
                        }}
                      >
                        V1.x
                      </span>
                    </label>
                    <input
                      type="text"
                      className="field-input"
                      value={inferInternalCode(account)}
                      disabled
                      placeholder="HDFC-CURR-4421"
                    />
                    <span className="field-help">
                      Used to map to the Tally / chart-of-accounts ledger on export.
                    </span>
                  </div>
                </div>

                {/* Row 2 — Account type segmented control (locked after creation) */}
                <div style={{ marginTop: 14 }}>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--ink-2-local)",
                      display: "block",
                      marginBottom: 6,
                    }}
                  >
                    Account type{" "}
                    <span className="req" style={{ color: "var(--rose)" }}>
                      *
                    </span>
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 12,
                        color: "var(--muted-local)",
                        fontWeight: 500,
                        textTransform: "none",
                        letterSpacing: 0,
                      }}
                    >
                      · locked after creation
                    </span>
                  </label>
                  <div className="seg-group" role="radiogroup" aria-label="Account type">
                    <TypeRadio
                      active={isType(account, "current")}
                      disabled
                      label="Current"
                      icon={<Landmark className="ic" strokeWidth={2} aria-hidden />}
                    />
                    <TypeRadio
                      active={isType(account, "savings")}
                      disabled
                      v1x
                      label="Savings"
                      icon={<Clock3 className="ic" strokeWidth={2} aria-hidden />}
                    />
                    <TypeRadio
                      active={isType(account, "od")}
                      disabled
                      v1x
                      label="Overdraft / CC"
                      icon={<Sparkles className="ic" strokeWidth={2} aria-hidden />}
                    />
                    <TypeRadio
                      active={isType(account, "credit_card")}
                      disabled
                      label="Credit card"
                      icon={<CreditCard className="ic" strokeWidth={2} aria-hidden />}
                    />
                    <TypeRadio
                      active={isType(account, "wallet")}
                      disabled
                      v1x
                      label="Wallet / UPI"
                      icon={<Wallet className="ic" strokeWidth={2} aria-hidden />}
                    />
                    <TypeRadio
                      active={isType(account, "cash")}
                      disabled
                      label="Cash on hand"
                      icon={<Wallet className="ic" strokeWidth={2} aria-hidden />}
                    />
                  </div>
                </div>

                {/* Row 3 — Currency + Group on chart of accounts (V1.x disabled) */}
                <div className="field-grid cols-2" style={{ marginTop: 14 }}>
                  <div className="field">
                    <label>
                      Currency <span className="req">*</span>
                    </label>
                    <Select value="INR" disabled>
                      <SelectTrigger className="!h-9 !text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INR">INR · Indian Rupee (₹)</SelectItem>
                        <SelectItem value="USD">USD · US Dollar ($)</SelectItem>
                        <SelectItem value="EUR">EUR · Euro (€)</SelectItem>
                        <SelectItem value="GBP">GBP · British Pound (£)</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="field-help">
                      All postings to this account are stored in this currency.
                    </span>
                  </div>
                  <div className="field">
                    <label>
                      Group on chart of accounts{" "}
                      <span
                        style={{
                          marginLeft: 4,
                          padding: "1px 6px",
                          borderRadius: 3,
                          background: "var(--bg-local)",
                          color: "var(--muted-local)",
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.06,
                          textTransform: "uppercase",
                          border: "1px solid var(--line-local)",
                        }}
                      >
                        V1.x
                      </span>
                    </label>
                    <Select disabled value={defaultCoaGroup(account.type)}>
                      <SelectTrigger className="!h-9 !text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={defaultCoaGroup(account.type)}>
                          {defaultCoaGroup(account.type)}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="field-help">
                      Drives placement in trial balance &amp; balance sheet. Lands with V2 GL
                      projection.
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2 — Bank & KYC details (mirror prototype lines 1273-1395) */}
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>
                    <span className="step">2</span> Bank &amp; KYC details
                  </h3>
                  <p className="sub">
                    Bank, branch, IFSC and the account holder on record. IFSC is validated against
                    RBI directory.
                  </p>
                </div>
                <div className="right">
                  {/* Bank type only — cash + cc don't need KYC verification. */}
                  {account.type === "bank" ? (
                    <span className="pill v1x">
                      <Check size={9} strokeWidth={3} aria-hidden />
                      KYC · V1.x
                    </span>
                  ) : (
                    <span className="pill v1x">N/A · {labelForType(account.type)}</span>
                  )}
                </div>
              </div>
              <div className={`section-body ${account.type !== "bank" ? "v1x-disabled" : ""}`}>
                {/* Row 1 — Bank + IFSC (with Verified affix) + Branch (cols-3-2) */}
                <div className="field-grid cols-3-2">
                  <div className="field">
                    <label>
                      Bank <span className="req">*</span>
                    </label>
                    <Select disabled value={inferBankFromName(account.name) || "—"}>
                      <SelectTrigger className="!h-9 !text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="—">—</SelectItem>
                        <SelectItem value="HDFC Bank">HDFC Bank</SelectItem>
                        <SelectItem value="ICICI Bank">ICICI Bank</SelectItem>
                        <SelectItem value="State Bank of India">State Bank of India</SelectItem>
                        <SelectItem value="Axis Bank">Axis Bank</SelectItem>
                        <SelectItem value="Kotak Mahindra Bank">Kotak Mahindra Bank</SelectItem>
                        <SelectItem value="RBL Bank">RBL Bank</SelectItem>
                        <SelectItem value="Yes Bank">Yes Bank</SelectItem>
                        <SelectItem value="IndusInd Bank">IndusInd Bank</SelectItem>
                        <SelectItem value="IDFC First Bank">IDFC First Bank</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="field-help">
                      Auto-locked from the IFSC code below — change only if the IFSC is wrong.{" "}
                      <span
                        style={{
                          marginLeft: 6,
                          padding: "1px 6px",
                          borderRadius: 3,
                          background: "var(--bg-local)",
                          color: "var(--muted-local)",
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.06,
                          textTransform: "uppercase",
                          border: "1px solid var(--line-local)",
                        }}
                      >
                        V1.x
                      </span>
                    </span>
                  </div>
                  <div className="field is-mono">
                    <label>
                      IFSC code <span className="req">*</span>
                    </label>
                    <div className="field-affix">
                      <input
                        className="field-input"
                        type="text"
                        value={account.ifscPlaintext ?? ""}
                        placeholder="HDFC0001247"
                        disabled
                      />
                      <span className={`badge ${account.ifscPlaintext ? "" : "muted"}`}>
                        {account.ifscPlaintext ? (
                          <>
                            <Check size={9} strokeWidth={3} aria-hidden />
                            Stored
                          </>
                        ) : (
                          "V1.x"
                        )}
                      </span>
                    </div>
                    <span className={`field-help ${account.ifscPlaintext ? "is-ok" : ""}`}>
                      {account.ifscPlaintext
                        ? `Encrypted at rest · ${maskIfsc(account.ifscPlaintext)}`
                        : "pgcrypto wrap lands in V1.0.x · 11-character RBI code"}
                    </span>
                  </div>
                  <div className="field">
                    <label>Branch</label>
                    <input
                      type="text"
                      className="field-input"
                      placeholder="Bhosari MIDC"
                      disabled
                    />
                    <span className="field-help">
                      Auto-filled from IFSC. Edit if printed differently on cheques.{" "}
                      <span
                        style={{
                          marginLeft: 6,
                          padding: "1px 6px",
                          borderRadius: 3,
                          background: "var(--bg-local)",
                          color: "var(--muted-local)",
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.06,
                          textTransform: "uppercase",
                          border: "1px solid var(--line-local)",
                        }}
                      >
                        V1.x
                      </span>
                    </span>
                  </div>
                </div>

                {/* Sensitive-locked S32 trigger card */}
                <div style={{ marginTop: 14 }}>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--ink-2-local)",
                      display: "block",
                      marginBottom: 6,
                    }}
                  >
                    Account number &amp; IFSC{" "}
                    <span className="req" style={{ color: "var(--rose)" }}>
                      *
                    </span>
                  </label>
                  <div className="sensitive-locked">
                    <div className="lock-ic" aria-hidden="true">
                      <Lock size={16} strokeWidth={2} aria-hidden />
                    </div>
                    <div className="lock-text">
                      <strong>
                        Current:{" "}
                        <span className="mono">
                          {account.accountNumberLast4
                            ? `····${account.accountNumberLast4}`
                            : "— (no credentials set)"}
                          {account.ifscPlaintext ? ` · ${account.ifscPlaintext}` : ""}
                        </span>
                      </strong>
                      <span>
                        Changing these requires Founder OTP + 24-hour cooling-off before any
                        outbound payment can use the new credentials.
                      </span>
                    </div>
                    <button
                      type="button"
                      className="unlock"
                      onClick={openCredsModal}
                      aria-haspopup="dialog"
                      disabled={isCoolingOff(account.bankDetailsChangedAt)}
                      title={
                        isCoolingOff(account.bankDetailsChangedAt)
                          ? "Cooling-off active — wait until the 24-hour window ends before the next change."
                          : "Change the account number + IFSC. Requires Founder OTP + 24-hour cooling-off (ADR-0008)."
                      }
                    >
                      <Pencil size={12} strokeWidth={2} aria-hidden />
                      Change →
                    </button>
                  </div>
                  {/* Cooling-off banner — only after a successful change. */}
                  {isCoolingOff(account.bankDetailsChangedAt) ? (
                    <div className="cooling-banner">
                      <Clock3 size={12} strokeWidth={2} aria-hidden />
                      <div>
                        <strong>24-hour cooling-off active.</strong>{" "}
                        <span>
                          Outbound payments from this account are locked until{" "}
                          <span className="countdown">
                            {formatCoolingOffEnd(account.bankDetailsChangedAt!)}
                          </span>
                          .
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Row 3 — Holder name + Holder type + SWIFT/BIC (cols-3).
                    Read-only on /edit: these fields are persisted on create
                    via migration 0023; inline editing of these non-sensitive
                    fields is V1.0.x (no updateBankAccountKyc procedure yet). */}
                <div className="field-grid cols-3" style={{ marginTop: 14 }}>
                  <div className="field">
                    <label>Account holder name</label>
                    <input
                      type="text"
                      className="field-input"
                      value={account.holderName ?? ""}
                      placeholder="—"
                      disabled
                    />
                    <span className="field-help">
                      Must match the name on the cheque / passbook for vendor payouts to clear.
                    </span>
                  </div>
                  <div className="field">
                    <label>Holder type</label>
                    <Select disabled value={account.holderType ?? "none"}>
                      <SelectTrigger className="!h-9 !text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        <SelectItem value="company">Company</SelectItem>
                        <SelectItem value="founder_personal">Founder personal</SelectItem>
                        <SelectItem value="partner">Partner</SelectItem>
                        <SelectItem value="director">Director</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="field-help">
                      Drives the GST / TDS challan signatory and the auto-narration on the printed
                      invoice.
                    </span>
                  </div>
                  <div className="field is-mono">
                    <label>SWIFT / BIC</label>
                    <input
                      type="text"
                      className="field-input"
                      value={account.swiftBic ?? ""}
                      placeholder="Optional · e.g. HDFCINBB"
                      disabled
                    />
                    <span className="field-help">Required only for foreign-currency wires.</span>
                  </div>
                </div>

                {/* Row 4 — Branch + MICR + Linked GSTIN slot (cols-3) */}
                <div className="field-grid cols-3" style={{ marginTop: 14 }}>
                  <div className="field">
                    <label>Branch</label>
                    <input
                      type="text"
                      className="field-input"
                      value={account.branch ?? ""}
                      placeholder="—"
                      disabled
                    />
                    <span className="field-help">
                      From RBI directory at the time of credentials change.
                    </span>
                  </div>
                  <div className="field is-mono">
                    <label>MICR code</label>
                    <input
                      type="text"
                      className="field-input"
                      value={account.micrCode ?? ""}
                      placeholder="9 digits"
                      disabled
                    />
                    <span className="field-help">Printed at the bottom of cheques. Optional.</span>
                  </div>
                  <div className="field">
                    <label>
                      Linked GSTIN bank slot{" "}
                      <span
                        style={{
                          marginLeft: 4,
                          padding: "1px 6px",
                          borderRadius: 3,
                          background: "var(--bg-local)",
                          color: "var(--muted-local)",
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.06,
                          textTransform: "uppercase",
                          border: "1px solid var(--line-local)",
                        }}
                      >
                        V1.x
                      </span>
                    </label>
                    <Select disabled value="slot1">
                      <SelectTrigger className="!h-9 !text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="slot1">Slot 1 — primary on GST portal</SelectItem>
                        <SelectItem value="slot2">Slot 2 — secondary</SelectItem>
                        <SelectItem value="not_linked">Not linked to GST portal</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="field-help">
                      Required under Rule 10A within 45 days of registration.
                    </span>
                  </div>
                </div>
              </div>
              <div className="section-foot">
                <Shield size={13} strokeWidth={2} aria-hidden />
                Changing credentials triggers a step-up OTP + 24-hour cooling-off before outbound
                transfers resume. Step-up middleware lands in V1.0.x.
              </div>
            </section>

            {/* Section 3 — Opening balance & reconciliation start
                (mirror prototype/cash/bank-edit.html lines 1397-1449) */}
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>
                    <span className="step">3</span> Opening balance &amp; reconciliation start
                  </h3>
                  <p className="sub">
                    The balance Flint should treat as the starting point. Used as the first row when
                    matching statement to ledger.
                  </p>
                </div>
                <div className="right">
                  <span className="pill warn">Affects reconciliation</span>
                </div>
              </div>
              <div className="section-body">
                {/* AI suggestion banner — wires when statement-import lands; static copy for now. */}
                <div className="ai-hint">
                  <Sparkles size={13} strokeWidth={2} aria-hidden />
                  <div className="body">
                    <strong>Flint suggestion ·</strong> Set the opening balance to the close of the
                    prior financial year. Once the first statement is imported this banner will
                    auto-fill the row, so you can skip the manual reconciliation entry.
                  </div>
                  <button type="button" disabled title="Statement-import lands in V1.x">
                    Use suggestion
                  </button>
                </div>

                <div className="field-grid cols-3-2" style={{ marginTop: 14 }}>
                  <div className="field is-mono">
                    <label>
                      Opening balance <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      className="field-input"
                      value={formatRupeesNoSymbol(BigInt(account.openingBalancePaise))}
                      disabled
                    />
                    <span className="field-help">
                      Positive for funds in account, negative for OD drawn. Locked after first money
                      event.
                    </span>
                  </div>
                  <div className="field is-mono">
                    <label>
                      As of date <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      className="field-input"
                      value={formatDate(account.openingBalanceAsOf)}
                      disabled
                    />
                    <span className="field-help">
                      Books beginning · {formatDate(account.openingBalanceAsOf)} (FY{" "}
                      {financialYearFor(account.openingBalanceAsOf)})
                    </span>
                  </div>
                  <div className="field">
                    <label>
                      Reconcile against <V1xChip />
                    </label>
                    <Select disabled value="opening_journal">
                      <SelectTrigger className="!h-9 !text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="opening_journal">Opening journal entry</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="field-help">Drives the very first row of the BRS.</span>
                  </div>
                </div>

                {account.type === "cash" || account.type === "credit_card" ? (
                  <div className="field-grid cols-2" style={{ marginTop: 14 }}>
                    {account.type === "cash" ? (
                      <div className="field is-mono">
                        <label>Imprest ceiling</label>
                        <input
                          type="text"
                          className="field-input"
                          value={
                            account.imprestAmountPaise
                              ? formatRupeesNoSymbol(BigInt(account.imprestAmountPaise))
                              : ""
                          }
                          disabled
                        />
                        <span className="field-help">
                          Drawer balance must stay at or below this ceiling.
                        </span>
                      </div>
                    ) : null}
                    {account.type === "credit_card" ? (
                      <>
                        <div className="field is-mono">
                          <label>Credit limit</label>
                          <input
                            type="text"
                            className="field-input"
                            value={
                              account.creditLimitPaise
                                ? formatRupeesNoSymbol(BigInt(account.creditLimitPaise))
                                : ""
                            }
                            disabled
                          />
                        </div>
                        <div className="field is-mono">
                          <label>Statement cycle day (1-31)</label>
                          <input
                            type="text"
                            className="field-input"
                            value={account.statementCycleDay ?? ""}
                            disabled
                          />
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : null}

                <div
                  className="toggle-row"
                  style={{ marginTop: 14, borderTop: "1px solid var(--line-local)" }}
                >
                  <div className="body">
                    <strong>Lock historical entries</strong>
                    <span>
                      Prevent any postings dated before{" "}
                      <span className="num">{formatDate(account.openingBalanceAsOf)}</span>.
                      Recommended once the books beginning is signed off.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="switch is-on"
                    role="switch"
                    aria-checked="true"
                    aria-label="Lock historical entries"
                    disabled
                    title="Configurable once V1.x books-lock lands"
                  />
                </div>
              </div>
            </section>

            {/* Section 4 — Connection & sync
                (mirror prototype/cash/bank-edit.html lines 1451-1549) */}
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>
                    <span className="step">4</span> Connection &amp; sync
                  </h3>
                  <p className="sub">
                    How transactions land in Flint. Pick auto-feed for daily sync, statement upload
                    for monthly close, or manual for petty / dormant accounts.
                  </p>
                </div>
                <div className="right">
                  <span className="pill v1x">V1.x</span>
                </div>
              </div>
              <div className="section-body">
                <div className="conn-grid" role="radiogroup" aria-label="Connection method">
                  <button
                    type="button"
                    className="conn-card"
                    role="radio"
                    aria-checked="false"
                    disabled
                  >
                    <span className="v1x-chip">V1.x</span>
                    <span className="check" aria-hidden="true" />
                    <span className="ic" aria-hidden="true">
                      <RefreshCcw size={14} strokeWidth={2} aria-hidden />
                    </span>
                    <strong>Auto-sync (bank feed)</strong>
                    <span>Yodlee · refreshes every 4 hours · 18 months of history pulled in</span>
                  </button>
                  <button
                    type="button"
                    className="conn-card"
                    role="radio"
                    aria-checked="false"
                    disabled
                  >
                    <span className="v1x-chip">V1.x</span>
                    <span className="check" aria-hidden="true" />
                    <span className="ic" aria-hidden="true">
                      <FileUp size={14} strokeWidth={2} aria-hidden />
                    </span>
                    <strong>Statement upload</strong>
                    <span>Drop CSV / PDF / Excel statements. Flint parses and de-duplicates.</span>
                  </button>
                  <button
                    type="button"
                    className="conn-card is-on"
                    role="radio"
                    aria-checked="true"
                  >
                    <span className="check" aria-hidden="true" />
                    <span className="ic" aria-hidden="true">
                      <Pencil size={12} strokeWidth={2} aria-hidden />
                    </span>
                    <strong>Manual entry</strong>
                    <span>
                      Every receipt / vendor payment / cash movement is booked manually against this
                      account.
                    </span>
                  </button>
                </div>

                <div className="sync-card">
                  <span className="ic" aria-hidden="true">
                    <RefreshCcw size={14} strokeWidth={2} aria-hidden />
                  </span>
                  <div className="body">
                    <strong>Manual · operator-driven</strong>
                    <small>
                      No external feed connected · all txns are written via /receipts, /payments,
                      /cash-movements.
                    </small>
                    <div className="row">
                      <span>
                        Frequency · <span className="v">On every write</span>
                      </span>
                      <span>
                        Last sync ·{" "}
                        <span className="v">{formatRelativeTime(account.updatedAt)}</span>
                      </span>
                      <span>
                        Next sync · <span className="v">—</span>
                      </span>
                      <span>
                        History pulled · <span className="v">N/A</span>
                      </span>
                    </div>
                  </div>
                  <div className="actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled
                      title="Auto-feed lands in V1.x"
                    >
                      <RefreshCcw size={14} strokeWidth={2} aria-hidden />
                      Sync now
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled
                      title="Auto-feed lands in V1.x"
                    >
                      Re-auth
                    </button>
                  </div>
                </div>

                <div className="field-grid cols-3" style={{ marginTop: 14 }}>
                  <div className="field">
                    <label>
                      Refresh frequency <V1xChip />
                    </label>
                    <Select disabled value="manual">
                      <SelectTrigger className="!h-9 !text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="field">
                    <label>
                      Auto-categorise rules <V1xChip />
                    </label>
                    <Select disabled value="off">
                      <SelectTrigger className="!h-9 !text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="off">Off — review every txn</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="field-help">
                      Flint AI rules ride on the auto-sync rollout.
                    </span>
                  </div>
                  <div className="field">
                    <label>
                      Daily statement email <V1xChip />
                    </label>
                    <Select disabled value="off">
                      <SelectTrigger className="!h-9 !text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="off">Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 5 — Behaviour & defaults
                (mirror prototype/cash/bank-edit.html lines 1551-1620) */}
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>
                    <span className="step">5</span> Behaviour &amp; defaults
                  </h3>
                  <p className="sub">
                    Where this account shows up across Flint — receipts, payouts, payroll, invoice
                    footer, UPI QR.
                  </p>
                </div>
                <div className="right">
                  <span className="pill v1x">V1.x</span>
                </div>
              </div>
              <div className="section-body" style={{ padding: "8px 18px" }}>
                <div className="toggle-row">
                  <div className="body">
                    <strong>Primary account for sales receipts</strong>
                    <span>
                      UPI / IMPS / NEFT collections from customers default here. /receipts will
                      pre-fill this account once the picker lands.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="switch"
                    role="switch"
                    aria-checked="false"
                    aria-label="Primary for sales receipts"
                    disabled
                  />
                </div>

                <div className="toggle-row">
                  <div className="body">
                    <strong>Default account for vendor payouts</strong>
                    <span>
                      NEFT / RTGS / IMPS transfers initiated from Bills will draw from this account
                      unless overridden.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="switch"
                    role="switch"
                    aria-checked="false"
                    aria-label="Default for vendor payouts"
                    disabled
                  />
                </div>

                <div className="toggle-row">
                  <div className="body">
                    <strong>Use for payroll runs</strong>
                    <span>Payroll funding draws from this account. Lands with V1.x payroll.</span>
                  </div>
                  <button
                    type="button"
                    className="switch"
                    role="switch"
                    aria-checked="false"
                    aria-label="Use for payroll"
                    disabled
                  />
                </div>

                <div className="toggle-row">
                  <div className="body">
                    <strong>Show on invoice "Pay to" block</strong>
                    <span>
                      Renders bank, IFSC and last 4 in the printed / WhatsApp invoice footer. Hides
                      automatically on credit-note &amp; quote PDFs.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="switch is-on"
                    role="switch"
                    aria-checked="true"
                    aria-label="Show on invoice"
                    disabled
                    title="Currently always-on for the primary bank — wires to a per-account toggle in V1.x"
                  />
                </div>

                <div className="toggle-row">
                  <div className="body">
                    <strong>Generate UPI QR with each invoice</strong>
                    <span>
                      Customers can scan to pay directly. Reconciles the txn automatically when
                      settled.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="switch"
                    role="switch"
                    aria-checked="false"
                    aria-label="Generate UPI QR"
                    disabled
                  />
                </div>

                <div className="toggle-row">
                  <div className="body">
                    <strong>Surface in cash trend on home</strong>
                    <span>Include this account in the dashboard's 30-day cash position chart.</span>
                  </div>
                  <button
                    type="button"
                    className="switch is-on"
                    role="switch"
                    aria-checked="true"
                    aria-label="Surface in cash trend"
                    disabled
                  />
                </div>
              </div>
            </section>
          </main>

          {/* ── RIGHT: aside ─────────────────────────────── */}
          <aside className="composer-aside" aria-label="Live preview &amp; meta">
            {/* Live preview (mirror prototype lines 1702-1749) */}
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>
                    <Eye size={13} strokeWidth={2} aria-hidden />
                    Live preview
                  </h3>
                  <p className="sub">How this account appears across Flint.</p>
                </div>
              </div>
              <div
                className="section-body"
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                {(() => {
                  const last4 = effectiveLast4(account);
                  const company = parseAccountName(name || "—").bank;
                  return (
                    <>
                      <div>
                        <div className="preview-section-h">In bank accounts list</div>
                        <div className="preview-list-row">
                          <div className={`preview-ava ${ava.classMod}`} aria-hidden="true">
                            {ava.label}
                          </div>
                          <div className="preview-row-body">
                            <strong>
                              {company}
                              {last4 ? (
                                <span className="acc">
                                  {labelForType(account.type)} ···{last4}
                                </span>
                              ) : null}
                            </strong>
                            <small>
                              {labelForType(account.type)} ·{" "}
                              {account.archivedAt ? "archived" : "active"}
                            </small>
                          </div>
                          <div className="preview-row-bal">
                            ₹{formatRupeesShort(BigInt(accountQ.data.runningBalancePaise))}
                            <small>available</small>
                          </div>
                        </div>
                      </div>

                      {account.type === "bank" ? (
                        <div>
                          <div className="preview-section-h">On invoice footer</div>
                          <div className="pay-block">
                            <div className="h">Pay to · NEFT / RTGS / IMPS</div>
                            <div className="row">
                              <span>Bank</span>
                              <strong className="is-sans">{company}</strong>
                            </div>
                            <div className="row">
                              <span>A/c name</span>
                              <strong className="is-sans">{company}</strong>
                            </div>
                            <div className="row">
                              <span>A/c no</span>
                              <strong>{last4 ? `········${last4}` : "—"}</strong>
                            </div>
                            <div className="row">
                              <span>IFSC</span>
                              <strong>{account.ifscPlaintext ?? "—"}</strong>
                            </div>
                            <div className="qr-row">
                              <div className="qr" aria-hidden="true" />
                              <div className="qr-meta">
                                <strong>Scan &amp; pay (UPI)</strong>
                                <small>UPI QR generation lands in V1.x</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </>
                  );
                })()}
              </div>
            </section>

            {/* Recent statement imports (mirror prototype lines 1751-1808) */}
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>
                    <FileText size={13} strokeWidth={2} aria-hidden />
                    Recent statement imports
                  </h3>
                  <p className="sub">Auto-pulled from Yodlee.</p>
                </div>
                <div className="right">
                  <span className="pill v1x">V1.x</span>
                </div>
              </div>
              <div className="section-body" style={{ padding: "6px 18px 12px" }}>
                <div className="stmt-list">
                  <p
                    style={{
                      fontSize: 12,
                      color: "var(--muted-local)",
                      margin: "6px 0",
                      lineHeight: 1.5,
                    }}
                  >
                    Statement-import history surfaces here once the auto-feed / CSV-upload adapters
                    land. Until then, every txn is sourced from the operator's manual entries.
                  </p>
                </div>
              </div>
            </section>

            {/* Audit trail (mirror prototype lines 1810-1853) */}
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>
                    <Shield size={13} strokeWidth={2} aria-hidden />
                    Audit trail
                  </h3>
                  <p className="sub">Every change to this account.</p>
                </div>
                <div className="right">
                  <span className="pill v1x">V1.x</span>
                </div>
              </div>
              <div className="section-body" style={{ padding: "6px 18px 12px" }}>
                <div className="audit">
                  {/* Synthesised from the row's own timestamps. Full audit-read view lands V1.x. */}
                  {account.bankDetailsChangedAt ? (
                    <div className="audit-item">
                      <span className="who" aria-hidden="true">
                        RS
                      </span>
                      <div>
                        <div>
                          <strong>Operator</strong> rotated bank credentials (step-up + 24h
                          cooling-off)
                        </div>
                      </div>
                      <span className="when">{formatShortDate(account.bankDetailsChangedAt)}</span>
                    </div>
                  ) : null}
                  <div className="audit-item">
                    <span className="who" aria-hidden="true">
                      RS
                    </span>
                    <div>
                      <div>
                        <strong>Operator</strong> updated this account
                      </div>
                    </div>
                    <span className="when">{formatShortDate(account.updatedAt)}</span>
                  </div>
                  <div className="audit-item">
                    <span className="who is-ai" aria-hidden="true">
                      L
                    </span>
                    <div>
                      <div>
                        <strong>Flint</strong> set opening balance to{" "}
                        <span className="num">
                          ₹{formatRupeesNoSymbol(BigInt(account.openingBalancePaise))}
                        </span>
                      </div>
                    </div>
                    <span className="when">{formatShortDate(account.openingBalanceAsOf)}</span>
                  </div>
                </div>
                <p
                  style={{
                    fontSize: 12,
                    color: "var(--muted-local)",
                    margin: "10px 0 0",
                    lineHeight: 1.5,
                  }}
                >
                  Synthesised view — the full chronological `audit_entries` query lands in V1.x.
                </p>
              </div>
            </section>

            {/* Danger zone (mirror prototype lines 1855-1882) */}
            <section className="danger-zone" aria-label="Danger zone">
              <h4>
                <AlertTriangle size={13} strokeWidth={2} aria-hidden />
                Danger zone
              </h4>
              <div className="row">
                <div className="body">
                  <strong>Mark dormant</strong>
                  <span>
                    Hides from default lists &amp; KPIs. Ledger is preserved. Reverse anytime by
                    un-archiving.
                  </span>
                </div>
                <button
                  type="button"
                  className="btn-danger"
                  disabled={archive.isPending || account.archivedAt !== null}
                  onClick={() => setDormantOpen(true)}
                >
                  {account.archivedAt
                    ? "Already dormant"
                    : archive.isPending
                      ? "Marking…"
                      : "Mark dormant"}
                </button>
                <ConfirmDialog
                  open={dormantOpen}
                  onOpenChange={setDormantOpen}
                  title="Mark this account dormant?"
                  description="It will be hidden from active lists & KPIs. The ledger is preserved and you can un-archive anytime."
                  confirmLabel="Mark dormant"
                  pending={archive.isPending}
                  onConfirm={() => {
                    setErrorMsg(null);
                    archive.mutate({ bankAccountId });
                  }}
                />
              </div>
              <div className="row">
                <div className="body">
                  <strong>Disconnect bank feed</strong>
                  <span>
                    Stops the auto-sync once it's enabled in V1.x. Statement upload will still work
                    and existing txns remain.
                  </span>
                </div>
                <button
                  type="button"
                  className="btn-danger"
                  disabled
                  title="Feed adapter lands in V1.x"
                >
                  Disconnect feed
                </button>
              </div>
              <div className="row">
                <div className="body">
                  <strong>Delete account</strong>
                  <span className="is-rose">
                    Blocked — we never hard-delete. Use <em>Mark dormant</em> above. The ledger and
                    audit trail stay intact for compliance.
                  </span>
                </div>
                <button type="button" className="btn-danger" disabled>
                  Delete
                </button>
              </div>
            </section>
          </aside>
        </div>
      </div>

      {/* Credentials change dialog — same S32 centered modal pattern as
          /new, wired to bankAccounts.changeBankDetails through the founder
          step-up gate (stepUpFounderProcedureFor, ADR-0008 / E03 fix). On
          success the row's bankDetailsChangedAt updates →
          isCoolingOff(account.bankDetailsChangedAt) flips true →
          cooling-off banner appears + Change button locks for 24h. */}
      {credsModalOpen ? (
        <CredsChangeDialog
          draftAccountNumber={draftAccountNumber}
          setDraftAccountNumber={setDraftAccountNumber}
          draftIfsc={draftIfsc}
          onDraftIfscChange={(v) => {
            setDraftIfsc(v);
            if (draftIfscLookup || draftIfscError) {
              setDraftIfscLookup(null);
              setDraftIfscError(null);
            }
          }}
          draftIfscLookup={draftIfscLookup}
          draftIfscError={draftIfscError}
          draftIfscLoading={draftIfscLoading}
          draftIfscIsWellFormed={draftIfscIsWellFormed}
          draftReady={draftReady}
          mutationError={credsModalError}
          isPending={changeBankDetails.isPending}
          onFetch={fetchDraftIfsc}
          onSave={saveCredsModal}
          onCancel={cancelCredsModal}
        />
      ) : null}
    </WorkspaceShell>
  );
}

// ── Credentials change dialog ────────────────────────────

interface CredsChangeDialogProps {
  draftAccountNumber: string;
  setDraftAccountNumber: (v: string) => void;
  draftIfsc: string;
  onDraftIfscChange: (v: string) => void;
  draftIfscLookup: {
    bank: string;
    branch: string;
    city: string;
    state: string;
    micr: string | null;
    swift: string | null;
  } | null;
  draftIfscError: string | null;
  draftIfscLoading: boolean;
  draftIfscIsWellFormed: boolean;
  draftReady: boolean;
  mutationError: string | null;
  isPending: boolean;
  onFetch: () => void;
  onSave: () => void;
  onCancel: () => void;
}

function CredsChangeDialog(props: CredsChangeDialogProps) {
  return (
    <div
      className="s32-scrim show"
      onClick={(e) => {
        if (e.target === e.currentTarget && !props.isPending) props.onCancel();
      }}
    >
      <div
        className="s32 show"
        role="dialog"
        aria-modal="true"
        aria-label="Change bank credentials"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="s32-head">
          <div className="s32-head-text">
            <h2>Change bank credentials</h2>
            <p>
              Enter the new account number + IFSC. Requires Founder OTP + 24-hour cooling-off before
              any outbound payment can use the new credentials (ADR-0008).
            </p>
          </div>
          <button
            type="button"
            className="s32-close"
            onClick={props.onCancel}
            aria-label="Close"
            disabled={props.isPending}
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="s32-body">
          <div className="field is-mono" style={{ marginBottom: 14 }}>
            <label>
              New account number <span className="req">*</span>
            </label>
            <input
              type="text"
              className="field-input"
              value={props.draftAccountNumber}
              onChange={(e) => props.setDraftAccountNumber(e.target.value)}
              placeholder="50100123456789"
              maxLength={64}
              autoFocus
              disabled={props.isPending}
            />
            <span className="field-help">6–64 digits. Envelope-wrapped at rest.</span>
          </div>
          <div className="field is-mono" style={{ marginBottom: 14 }}>
            <label>
              New IFSC code <span className="req">*</span>
            </label>
            <div className="field-affix">
              <input
                type="text"
                className="field-input"
                value={props.draftIfsc}
                onChange={(e) => props.onDraftIfscChange(e.target.value.toUpperCase())}
                placeholder="HDFC0001247"
                maxLength={11}
                disabled={props.isPending}
              />
              <button
                type="button"
                className="field-affix-action"
                onClick={props.onFetch}
                disabled={!props.draftIfscIsWellFormed || props.draftIfscLoading || props.isPending}
                title={
                  props.draftIfscIsWellFormed
                    ? "Fetch bank + branch from RBI directory"
                    : "Enter a valid 11-character IFSC first"
                }
              >
                {props.draftIfscLoading ? (
                  <LoaderCircle
                    size={12}
                    strokeWidth={2}
                    aria-hidden
                    style={{ animation: "flint-spin 0.8s linear infinite" }}
                  />
                ) : (
                  <Search size={12} strokeWidth={2} aria-hidden />
                )}
                {props.draftIfscLoading ? "Fetching…" : "Fetch"}
              </button>
            </div>
            <span
              className={`field-help${props.draftIfscLookup ? " is-ok" : props.draftIfscError ? " is-error" : ""}`}
            >
              {props.draftIfscLookup
                ? `✓ ${props.draftIfscLookup.bank} · ${props.draftIfscLookup.branch}, ${props.draftIfscLookup.city}`
                : props.draftIfscError
                  ? props.draftIfscError
                  : `11-char RBI code · ${props.draftIfsc.length}/11${props.draftIfscIsWellFormed ? " · click Fetch to verify" : ""}.`}
            </span>
          </div>

          {props.mutationError ? (
            <div
              style={{
                marginBottom: 14,
                padding: 12,
                background: "#fdf2f8",
                border: "1px solid #fce7f3",
                borderRadius: 6,
                color: "#db2777",
                fontSize: 12,
                lineHeight: 1.55,
              }}
            >
              <strong style={{ fontWeight: 700 }}>Couldn't save.</strong> {props.mutationError}
            </div>
          ) : null}

          <div
            style={{
              padding: 12,
              background: "#fefce8",
              border: "1px solid #fef9c3",
              borderRadius: 6,
              color: "#854d0e",
              fontSize: 12,
              lineHeight: 1.55,
            }}
          >
            <strong style={{ fontWeight: 700 }}>Founder OTP is required.</strong> A step-up
            verification runs before this change commits. After save, the 24-hour cooling-off window
            starts immediately — no outbound payment can use the new credentials until it ends.
          </div>
        </div>
        <div
          style={{
            padding: 14,
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            type="button"
            className="btn-secondary"
            onClick={props.onCancel}
            disabled={props.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={props.onSave}
            disabled={!props.draftReady || props.isPending}
          >
            <Check size={13} strokeWidth={2} aria-hidden />
            {props.isPending ? "Saving…" : "Save credentials"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Save status pill ─────────────────────────────────────

function SaveStatus({ status }: { status: "saved" | "dirty" | "saving" }) {
  const label =
    status === "saving" ? "Saving…" : status === "dirty" ? "Unsaved changes" : "All changes saved";
  const cls =
    status === "saved"
      ? "save-status"
      : status === "dirty"
        ? "save-status dirty"
        : "save-status saving";
  return (
    <span className={cls} aria-live="polite">
      {label}
    </span>
  );
}

/** Small inline `V1.x` chip used beside field labels for deferred fields. */
function V1xChip() {
  return (
    <span
      style={{
        marginLeft: 4,
        padding: "1px 6px",
        borderRadius: 3,
        background: "var(--bg-local)",
        color: "var(--muted-local)",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.06,
        textTransform: "uppercase",
        border: "1px solid var(--line-local)",
      }}
    >
      V1.x
    </span>
  );
}

// ── Account-type segmented-control button ────────────────

/**
 * One of the 6 type tiles in the Account-identity section's segmented
 * control. Mirrors `prototype/cash/bank-edit.html ▸ .seg-group button`
 * markup verbatim.
 *
 * For V1.0 we always render all 6 buttons disabled (type is locked
 * after creation), with `active` marking the one that matches the
 * row's stored `type` + name heuristic. The 3 V1.x-only sub-types
 * (Savings / Overdraft·CC / Wallet·UPI) carry a small `V1.x` chip so
 * the surface honestly signals what V1 supports.
 */
function TypeRadio({
  active,
  disabled,
  v1x = false,
  label,
  icon,
}: {
  active: boolean;
  disabled?: boolean;
  v1x?: boolean;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      className={active ? "is-active" : ""}
      disabled={disabled}
      title={
        disabled
          ? v1x
            ? `${label} sub-type lands in V1.x`
            : "Type is locked after creation"
          : undefined
      }
    >
      {icon}
      {label}
      {v1x ? (
        <span
          style={{
            marginLeft: 4,
            padding: "0px 4px",
            borderRadius: 3,
            background: "rgba(255,255,255,.16)",
            color: "inherit",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.06,
            textTransform: "uppercase",
            opacity: active ? 1 : 0.7,
          }}
        >
          V1.x
        </span>
      ) : null}
    </button>
  );
}

// ── Type-mapping helpers ────────────────────────────────

/** Account-type subtype tag the segmented control activates. */
type TypeSubtype = "current" | "savings" | "od" | "credit_card" | "wallet" | "cash";

/**
 * Map our stored `bank | cash | credit_card` onto the prototype's
 * 6 sub-type tiles. Heuristic by name keyword (Current / Savings)
 * so the prototype's richer surface degrades to our coarser V1 enum
 * without losing information.
 */
function isType(account: AccountWire, subtype: TypeSubtype): boolean {
  const n = account.name.toLowerCase();
  switch (account.type) {
    case "cash":
      return subtype === "cash";
    case "credit_card":
      return subtype === "credit_card";
    case "bank":
      // Heuristic: "savings" / "current" in the name, default to "current".
      if (n.includes("savings")) return subtype === "savings";
      if (n.includes("overdraft") || n.includes(" od ") || n.includes(" od·"))
        return subtype === "od";
      if (n.includes("wallet") || n.includes(" upi")) return subtype === "wallet";
      return subtype === "current";
  }
}

/**
 * Closest V2 CoA group label for this account type. Surfaced as the
 * default-selected option of the (disabled) Group-on-chart-of-accounts
 * dropdown so the operator sees where this row will land once the GL
 * projection ships.
 */
function defaultCoaGroup(type: "bank" | "cash" | "credit_card"): string {
  switch (type) {
    case "cash":
      return "Cash in hand";
    case "credit_card":
      return "Credit card liability";
    default:
      return "Bank accounts (current assets)";
  }
}

/**
 * Best-effort internal-code suggestion derived from name + last-4.
 * Shown as the disabled placeholder on the V1.x Internal-code field
 * so the operator sees the shape the column will hold once it lands.
 */
function inferInternalCode(account: AccountWire): string {
  if (!account.accountNumberLast4) return "";
  const bank = account.name.split(" · ")[0] ?? account.name;
  const slug = bank
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 12);
  return `${slug}-${account.accountNumberLast4}`;
}

/**
 * `true` while the 24-hour cooling-off window after a credentials
 * change is still open. Drives the `.cooling-banner` render below
 * the sensitive-locked card.
 */
function isCoolingOff(changedAtIso: string | null): boolean {
  if (!changedAtIso) return false;
  const COOLING_OFF_MS = 24 * 60 * 60 * 1000;
  const changedAt = new Date(changedAtIso).getTime();
  return Date.now() - changedAt < COOLING_OFF_MS;
}

/** Map our `name` string onto the Bank dropdown's <option> values. */
function inferBankFromName(name: string): string {
  const n = name.toLowerCase();
  if (n.includes("hdfc")) return "HDFC Bank";
  if (n.includes("icici")) return "ICICI Bank";
  if (n.includes("state bank") || n.startsWith("sbi")) return "State Bank of India";
  if (n.includes("axis")) return "Axis Bank";
  if (n.includes("kotak")) return "Kotak Mahindra Bank";
  if (n.includes("rbl")) return "RBL Bank";
  if (n.includes("yes bank")) return "Yes Bank";
  if (n.includes("indusind")) return "IndusInd Bank";
  if (n.includes("idfc")) return "IDFC First Bank";
  return "";
}

/**
 * Friendlier IFSC display — show first 4 chars (bank code) in a "Stored"
 * affix message without leaking the full string. The full plaintext is
 * decrypted only via the audited reveal path on /get.
 */
function maskIfsc(ifsc: string): string {
  if (ifsc.length < 4) return "····";
  return `${ifsc.slice(0, 4)}······`;
}

/**
 * Compute the end of the 24-hour cooling-off window for the
 * `cooling-banner`. Returns `HH:MM, DD MMM YYYY` matching the prototype.
 */
function formatCoolingOffEnd(changedAtIso: string): string {
  const COOLING_OFF_MS = 24 * 60 * 60 * 1000;
  const endsAt = new Date(new Date(changedAtIso).getTime() + COOLING_OFF_MS);
  const dd = String(endsAt.getDate()).padStart(2, "0");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const mmm = months[endsAt.getMonth()] ?? "";
  const hh = String(endsAt.getHours()).padStart(2, "0");
  const mm = String(endsAt.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}, ${dd} ${mmm} ${endsAt.getFullYear()}`;
}

// ── Helpers (shared with list/detail) ────────────────────

function labelForType(t: "bank" | "cash" | "credit_card"): string {
  switch (t) {
    case "cash":
      return "Petty cash";
    case "credit_card":
      return "Credit card";
    default:
      return "Operating bank";
  }
}

/**
 * Best-effort last-4 lookup. The structured `accountNumberLast4` column
 * is null while the seed still encodes the suffix inside `name`
 * ("HDFC Bank · Current ···4421"); strip it out for the preview surfaces
 * until the verification migration backfills the column.
 */
function effectiveLast4(account: {
  name: string;
  accountNumberLast4: string | null;
}): string | null {
  if (account.accountNumberLast4) return account.accountNumberLast4;
  const m = account.name.match(/···(\d{3,6})/);
  return m && m[1] ? m[1] : null;
}

function parseAccountName(name: string): { bank: string; acc: string | null } {
  const sep = " · ";
  const idx = name.indexOf(sep);
  if (idx === -1) return { bank: name, acc: null };
  return { bank: name.slice(0, idx), acc: name.slice(idx + sep.length) };
}

function avatarFor(account: { name: string; type: "bank" | "cash" | "credit_card" }): {
  classMod: string;
  label: string;
} {
  if (account.type === "cash") return { classMod: "b-cash", label: "₹" };
  if (account.type === "credit_card") return { classMod: "b-cc", label: "VISA" };
  const n = account.name.toLowerCase();
  if (n.includes("hdfc")) return { classMod: "b-hdfc", label: "H" };
  if (n.includes("icici")) return { classMod: "b-icici", label: "I" };
  if (n.includes("kotak")) return { classMod: "b-kotak", label: "K" };
  if (n.includes("axis")) return { classMod: "b-axis", label: "A" };
  if (n.includes("rbl")) return { classMod: "b-rbl", label: "R" };
  return { classMod: "b-hdfc", label: account.name.charAt(0).toUpperCase() };
}

function formatRupeesNoSymbol(paise: bigint): string {
  const abs = paise < 0n ? -paise : paise;
  const rupees = abs / 100n;
  return rupees.toLocaleString("en-IN");
}

function formatDate(iso: string): string {
  const d = new Date(iso.includes("T") ? iso : `${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Indian financial year string for the books-beginning hint copy.
 * FY runs Apr–Mar. `2026-04-01` → "2026-27". `2025-12-15` → "2025-26".
 */
function financialYearFor(iso: string): string {
  const d = new Date(iso.includes("T") ? iso : `${iso}T00:00:00Z`);
  const m = d.getUTCMonth(); // 0 = Jan
  const y = d.getUTCFullYear();
  const start = m >= 3 ? y : y - 1; // April or later → that calendar year; else prior year
  const endYY = String((start + 1) % 100).padStart(2, "0");
  return `${start}-${endYY}`;
}

/** Short-rupees formatter for KPI / preview surfaces — `₹8.42L`, `₹65.2K`, `₹420`. */
function formatRupeesShort(paise: bigint): string {
  const negative = paise < 0n;
  const abs = negative ? -paise : paise;
  const rupees = Number(abs / 100n);
  let out: string;
  if (rupees >= 10_000_000) out = `${(rupees / 10_000_000).toFixed(2)}Cr`;
  else if (rupees >= 100_000) out = `${(rupees / 100_000).toFixed(2)}L`;
  else if (rupees >= 1_000) out = `${(rupees / 1_000).toFixed(1)}K`;
  else out = rupees.toLocaleString("en-IN");
  return negative ? `-${out}` : out;
}

/** Audit-trail timestamp — `02 May · 14:18`. */
function formatShortDate(iso: string): string {
  const d = new Date(iso.includes("T") ? iso : `${iso}T00:00:00Z`);
  const dd = String(d.getDate()).padStart(2, "0");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const mmm = months[d.getMonth()] ?? "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const showTime = iso.includes("T");
  return showTime ? `${dd} ${mmm} · ${hh}:${mm}` : `${dd} ${mmm} ${d.getFullYear()}`;
}

/**
 * Relative-time formatter for the composer-head sub-line.
 * "5 min ago" / "2 hr ago" / "today" / "yesterday" / "12 May 2026".
 */
function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "yesterday";
  if (diffDay < 7) return `${diffDay} days ago`;
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// ── Icons ────────────────────────────────────────────────

/* Sections 3-5 + aside icons — verbatim path data from
   prototype/cash/bank-edit.html (the .ai-hint .spark, .conn-card .ic,
   .sync-card .ic, .preview .ic and .danger h4 .ic). */

/* The 6 type-tile icons — verbatim path data from
   prototype/cash/bank-edit.html lines 1230-1262 (the .seg-group buttons in
   the Account-identity segmented control). */

/* KYC-section icons — lifted from prototype/cash/bank-edit.html. */
